import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, ArrowsOut, CaretUp } from '@phosphor-icons/react';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';

/**
 * Format time in mm:ss format.
 */
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * MiniPlayer Component
 * Persistent bottom bar audio player.
 * Tapping the track info/thumbnail expands to the full-screen player.
 */
const MiniPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    isLoading,
    isPlayerVisible,
    isFullScreen,
    currentTime,
    duration,
    togglePlayPause,
    hidePlayer,
    toggleFullScreen,
  } = useAudioPlayer();

  if (!isPlayerVisible || isFullScreen || !currentTrack) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#1a1a1a] border-t border-gray-800"
        data-testid="mini-player"
      >
        {/* Expand hint bar — pulses gently to draw attention */}
        <button
          onClick={toggleFullScreen}
          className="w-full flex items-center justify-center gap-1.5 py-1 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer border-0"
          data-testid="mini-player-expand-hint"
        >
          <CaretUp size={14} weight="bold" className="text-white/50" />
          <span className="text-[11px] text-white/50 font-medium tracking-wide uppercase">
            Tap to expand player
          </span>
          <CaretUp size={14} weight="bold" className="text-white/50" />
        </button>

        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center gap-3">
          {/* Thumbnail — tappable to expand */}
          <button
            onClick={toggleFullScreen}
            className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-800 relative group cursor-pointer border-0"
            data-testid="mini-player-cover"
          >
            <img
              src={currentTrack.cover_image_url || 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=200'}
              alt={currentTrack.title}
              className="w-full h-full object-cover"
            />
            {/* Expand icon overlay — always visible on mobile */}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <ArrowsOut size={20} className="text-white/80" />
            </div>
          </button>

          {/* Track Info — tappable to expand */}
          <button
            onClick={toggleFullScreen}
            className="flex-1 text-left min-w-0 cursor-pointer border-0 bg-transparent"
          >
            <h4 className="text-white font-medium text-sm truncate">
              {currentTrack.title}
            </h4>
            <p className="text-gray-400 text-xs">
              Audio Tour &middot; {formatTime(currentTime)} / {formatTime(duration)}
            </p>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="w-14 h-10 rounded-full bg-[#8B4513] hover:bg-[#A0522D] flex items-center justify-center transition-colors disabled:opacity-50"
              data-testid="mini-player-toggle"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause size={22} weight="fill" className="text-white" />
              ) : (
                <Play size={22} weight="fill" className="text-white ml-0.5" />
              )}
            </button>

            <button
              onClick={hidePlayer}
              className="w-10 h-10 rounded-full bg-[#8B2942] hover:bg-[#A03050] flex items-center justify-center transition-colors"
              data-testid="mini-player-close"
            >
              <X size={20} weight="bold" className="text-white" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MiniPlayer;
